import { useEffect, useRef, useState } from "react";
import { useLocation as useRouterLocation, useNavigate } from "react-router-dom";
import { HelpCircle, MessageCircle, MessagesSquare, BookOpen, X } from "lucide-react";
import SupportChatPanel from "./SupportChatPanel";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useWhatsAppAiAddon } from "@/hooks/useWhatsAppFloating";

/**
 * Botón flotante de AYUDA (12-sep-2026, petición de Luis): en naranja para
 * que se vea a la primera. Abre un mini-popover con las TRES vías de soporte:
 * chat integrado (SupportChatPanel — cae en /admin/soporte del superadmin),
 * WhatsApp al número de ReservaManager (atiende Carmen 24/7 con el equipo
 * detrás) y el Centro de Ayuda. Se oculta en las pantallas de chat (el compositor vive
 * abajo a la derecha) — misma razón por la que el antiguo botón de reservas
 * se ocultaba en /conversations.
 */
const HIDDEN_PATHS = ["/conversations", "/admin/soporte"];

const SUPPORT_WA_URL =
  "https://wa.me/34682249618?text=Hola%2C%20necesito%20ayuda%20con%20ReservaManager";

export default function FloatingHelpButton() {
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();
  // Con la bola de WhatsApp presente (negocio con WhatsApp IA), la ayuda se
  // hace pequeña y se va abajo a la IZQUIERDA — en todos los tamaños, para
  // que las dos bolas no se pisen en la misma esquina. Sin WhatsApp IA, la
  // ayuda conserva su sitio y tamaño de siempre.
  const { user } = useAuth();
  const { can } = usePermissions();
  // OJO: el hook se llama SIEMPRE (nunca tras un && que cortocircuite —
  // reglas de hooks); la condición se compone después.
  const hasWhatsAppAi = useWhatsAppAiAddon();
  // Superadmin (18-sep-2026): su bola de CONVERSACIONES ya no depende del
  // add-on (ver `FloatingConversationsButton.tsx`) — sin este OR, la ayuda no
  // se enteraba de que la bola verde estaba presente y las dos se montaban
  // encima en la esquina inferior derecha.
  const isSuperAdmin = user?.role === "owner";
  const hasWhatsAppBall =
    isSuperAdmin || (can("conversaciones:gestionar") && hasWhatsAppAi === true);
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cerrar al hacer clic fuera.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (HIDDEN_PATHS.some((p) => routerLocation.pathname.startsWith(p))) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`fixed bottom-10 sm:bottom-5 z-40 ${hasWhatsAppBall ? "left-5" : "right-5"}`}
    >
      {chatOpen && (
        <SupportChatPanel
          side={hasWhatsAppBall ? "left" : "right"}
          onClose={() => setChatOpen(false)}
        />
      )}
      {open && !chatOpen && (
        <div
          className={`absolute bottom-16 w-64 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden ${hasWhatsAppBall ? "left-0" : "right-0"}`}
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">
              ¿Necesitas ayuda?
            </p>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-gray-100 text-gray-400"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            onClick={() => {
              setOpen(false);
              setChatOpen(true);
            }}
            className="w-full flex items-start gap-3 px-4 py-3 hover:bg-primary-50 transition-colors text-left"
          >
            <MessagesSquare className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">
                Chat de soporte
              </span>
              <span className="block text-xs text-gray-500">
                Aquí mismo, respuesta al momento
              </span>
            </span>
          </button>
          <a
            href={SUPPORT_WA_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors border-t border-gray-100"
          >
            <MessageCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">
                Soporte por WhatsApp
              </span>
              <span className="block text-xs text-gray-500">
                Respuesta al momento, 24/7
              </span>
            </span>
          </a>
          <button
            onClick={() => {
              setOpen(false);
              navigate("/help");
            }}
            className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-t border-gray-100"
          >
            <BookOpen className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">
                Centro de Ayuda
              </span>
              <span className="block text-xs text-gray-500">
                Tutoriales y preguntas frecuentes
              </span>
            </span>
          </button>
        </div>
      )}
      <button
        onClick={() => {
          if (chatOpen) {
            setChatOpen(false);
            return;
          }
          setOpen((o) => !o);
        }}
        title="Ayuda"
        className={`rounded-full bg-orange-500 shadow-lg flex items-center justify-center text-white hover:bg-orange-600 hover:shadow-xl transition-all ${
          hasWhatsAppBall ? "w-10 h-10" : "w-14 h-14"
        }`}
      >
        <HelpCircle className={hasWhatsAppBall ? "w-5 h-5" : "w-7 h-7"} />
      </button>
    </div>
  );
}
